import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendRequest } from '../../../store/api';
import { addressUrl, cc_ordersUrl, deliveryAndSelf_orderUrl, pricesUrl, servicesUrl } from '../../../settings';
import { CustomerDetailsFormTypes } from '../../../components/FormHoc/Forms/types';
import i18n from '../../../i18nconfig';
import { calculateTotalPages, calculateTotalpagesPerFile, getFileIds, getStateAndToken } from '../../../utils/helpers';
import { Order } from '../../../utils/types';
import { FileInfo } from '../file/types';
import { OrderDeliveryAddress } from './orderTypes';

export const getServices = createAsyncThunk('order/getServices', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { uploadedFiles } = state.file;
    const totalPages = calculateTotalPages(uploadedFiles);

    const { data } = await sendRequest('get', `${servicesUrl}/?pages=${totalPages}`, null, `Bearer ${access_token}`);
    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

// The /prices/ request for the current order; overrides change options (e.g. the same order without extras)
const priceRequestBody = (state: any, overrides: Record<string, unknown> = {}) => {
  const { order, file, app, user } = state;
  return {
    is_color: order.options.is_color,
    is_duplex: order.options.is_duplex,
    copies: order.options.copies,
    document_ids: getFileIds(file.uploadedFiles),
    documents_pages: calculateTotalpagesPerFile(file.uploadedFiles),
    pages: calculateTotalPages(file.uploadedFiles),
    documents: file.uploadedFiles.length,
    delivery_type: order.delivery_type,
    service_name: order.service_type,
    lamination: order.options.lamination,
    binding: order.options.binding,
    ...(order.service_type === 'click_and_collect' && { location_id: app.selectedShop?.id }),
    ...(user.userType === 'guest' && order.guest_email && { is_guest: true, email: order.guest_email }),
    ...overrides,
  };
};

export const getPrices = createAsyncThunk('order/postPrices', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { data } = await sendRequest('post', pricesUrl, priceRequestBody(state), `Bearer ${access_token}`);

    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export type PriceBreakdown = { printing: number; binding?: number; lamination?: number };

// The API's printing subtotal includes binding and lamination. To show them as their own lines, price the same
// order without extras (and with binding only, when both are on) and take the differences, so the lines are the
// API's own figures and add up to its subtotal. null when there are no extras.
export const getPriceBreakdown = createAsyncThunk('order/getPriceBreakdown', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { binding, lamination } = state.order.options;
    if (!binding && !lamination) return null;

    const subtotal = async (overrides?: Record<string, unknown>) =>
      (await sendRequest('post', pricesUrl, priceRequestBody(state, overrides), `Bearer ${access_token}`)).data
        .subtotal as number;
    const [full, printing, withBinding] = await Promise.all([
      subtotal(),
      subtotal({ binding: false, lamination: false }),
      binding && lamination ? subtotal({ lamination: false }) : Promise.resolve(undefined),
    ]);

    const breakdown: PriceBreakdown = { printing };
    if (binding) breakdown.binding = (lamination ? (withBinding as number) : full) - printing;
    if (lamination) breakdown.lamination = full - (binding ? (withBinding as number) : printing);
    return breakdown;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const createSelfOrDeliveryService = createAsyncThunk('order/createOrder', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);

    const { userType } = state.user;
    const { order, file, app } = state;

    // Get address before proceeding
    const addressResponse = await sendRequest('get', addressUrl, null, `Bearer ${access_token}`);
    if (!addressResponse.data) {
      throw new Error('User has no address');
    }

    const documentsId = file.uploadedFiles.map((file: FileInfo) => Number(file.id));

    let printerId;
    if (order.service_type === 'self_service') {
      if (!app.selectedPrinter) {
        throw new Error('Selected printer is null or undefined');
      }
      printerId = app.selectedPrinter.id;
    } else if (order.service_type === 'click_and_collect') {
      if (!app.selectedShop) {
        throw new Error('Selected shop is null or undefined');
      }
      printerId = app.selectedShop.id;
    } else {
      // For delivery, there is no selected shop or printer
      printerId = null;
    }

    const body = {
      service_type: order.service_type,
      is_draft: false,
      printer: printerId,
      documents: documentsId,
      documents_pages: calculateTotalpagesPerFile(file.uploadedFiles),
      options: {
        is_color: order.options.is_color,
        is_duplex: order.options.is_duplex,
        copies: order.options.copies,
        delivery_type: order.delivery_type,
        lamination: order.options.lamination,
        binding: order.options.binding,
        paper_size: order.service_type === 'self_service' ? 'A4' : order.options.paper_size,
      },
      donation: order.pricing.donation ? order.pricing.donation : false,
      total_pages: calculateTotalPages(file.uploadedFiles),
      ...(userType === 'guest' && { guest_email: order.guest_email }),
    };

    const { data } = await sendRequest('post', deliveryAndSelf_orderUrl, body, `Bearer ${access_token}`);

    return data;
  } catch (error: any) {
    console.error('Error in createSelfOrDeliveryService:', error); // Debugging statement
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const createClickAndCollectOrder = createAsyncThunk('order/createCcOrder', async (_, thunkApi) => {
  try {
    const [state, access_token] = getStateAndToken(thunkApi);
    const { userType } = state.user;
    const { order, file, app } = state;

    const shopId = app.selectedShop?.id;

    const body = {
      document_ids: getFileIds(file.uploadedFiles),
      copies: order.options.copies,
      is_color: order.options.is_color,
      is_duplex: order.options.is_duplex,
      binding: order.options.binding,
      lamination: order.options.lamination,
      service_name: order.service_type,
      location_id: shopId,
      paper_size: order.options.paper_size,
      donation: order.pricing.donation,
      ...(userType === 'guest' && {
        guest_email: order.guest_email,
        guest_first_name: order.orderDeliveryAddress?.recipient_first_name,
        guest_last_name: order.orderDeliveryAddress?.recipient_last_name,
      }),
    };

    const { data } = await sendRequest('post', cc_ordersUrl, body, `Bearer ${access_token}`);
    return data;
  } catch (error: any) {
    const ApierrorMessage = error.response ? error.response.data[0] : error.message;
    const errorMessage = ApierrorMessage ? ApierrorMessage : `${i18n.t('messages.errorTryAgain')}`;
    return thunkApi.rejectWithValue(errorMessage);
  }
});

export const getOrder = createAsyncThunk(
  'order/getCurrentOrder',
  async (
    {
      orderId,
      service_type,
      printed_document_id,
    }: { orderId: number; service_type: string; printed_document_id?: number },
    thunkApi,
  ) => {
    try {
      const [state, access_token] = getStateAndToken(thunkApi);

      let url = '';
      if (service_type === 'click_and_collect') {
        url = `${cc_ordersUrl}${orderId}`;
      } else if (service_type === 'delivery' && printed_document_id) {
        url = `${deliveryAndSelf_orderUrl}${printed_document_id}`;
      } else {
        url = `${deliveryAndSelf_orderUrl}${orderId}`;
      }

      const { data } = await sendRequest('get', url, null, `Bearer ${access_token}`);
      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const cancelClickAndCollectOrder = createAsyncThunk(
  'order/cancelCcOrder',
  async ({ orderId }: { orderId: number }, thunkApi) => {
    try {
      const [state, access_token] = getStateAndToken(thunkApi);

      const orderData = {
        orderId,
      };
      const { data } = await sendRequest(
        'patch',
        `${cc_ordersUrl}${orderId}`,
        { status: 'canceled' },
        `Bearer ${access_token}`,
      );

      return data;
    } catch (error: any) {
      let errorMessage;
      if (error.response) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          `Error ${error.response.status}: ${error.response.statusText}`;
      } else {
        errorMessage = error.message || 'An unknown error occurred';
      }
      console.error('Cancel order error:', errorMessage);
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const getUserOrders = createAsyncThunk(
  'order/getUserOrders',
  async ({ orderQuery }: { orderQuery: any }, thunkApi) => {
    try {
      const [state, access_token] = getStateAndToken(thunkApi);
      const { data } = await sendRequest(
        'get',
        `${cc_ordersUrl}?limit=${51}&offset=0&query=${orderQuery}`,
        null,
        `Bearer ${access_token}`,
      );
      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

// Order Address APIs

export const createOrderAddress = createAsyncThunk(
  'order/createUserDeliveryAddress',
  async (formData: OrderDeliveryAddress | CustomerDetailsFormTypes, thunkApi) => {
    const [state, access_token] = getStateAndToken(thunkApi);
    try {
      const { data } = await sendRequest('post', addressUrl, formData, `Bearer ${access_token}`);
      thunkApi.dispatch(getOrderAddress());
      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const updateOrderAddress = createAsyncThunk(
  'order/createUserDeliveryAddress',
  async (formData: OrderDeliveryAddress | CustomerDetailsFormTypes, thunkApi) => {
    const [state, access_token] = getStateAndToken(thunkApi);
    try {
      const { data } = await sendRequest('put', addressUrl, formData, `Bearer ${access_token}`);
      thunkApi.dispatch(getOrderAddress());
      return data;
    } catch (error: any) {
      const errorMessage = error.response ? error.response.data.message : error.message;
      return thunkApi.rejectWithValue(errorMessage);
    }
  },
);

export const getOrderAddress = createAsyncThunk('order/getOrderAddress', async (_, thunkApi) => {
  const [state, access_token] = getStateAndToken(thunkApi);
  try {
    const { data } = await sendRequest('get', addressUrl, null, `Bearer ${access_token}`);
    return data;
  } catch (error: any) {
    const errorMessage = error.response ? error.response.data.message : error.message;
    return thunkApi.rejectWithValue(errorMessage);
  }
});
